import type {
  Domain,
  PhaseRecord,
  PlanDecisionInput,
  ProjectRecord,
  Projection,
  TriageBatchInput,
  UnphasedMilestone,
} from "./api";
import { addDays } from "./overview";
import { UNDO_MS } from "./triage";

/**
 * Pure logic for the phase Gantt (docs/plans/2026-10-27-P13-phase-gantt.md).
 * Dates are local calendar days (YYYY-MM-DD). Never parse a bare day with new Date(day):
 * that reads it as UTC. Use the T00:00:00 form, as lib/overview.ts does.
 */

export interface Range {
  start: string;
  end: string; // inclusive
}

// ── days ─────────────────────────────────────────────────────────────────────
function localDate(day: string): Date {
  return new Date(`${day}T00:00:00`);
}

/** Whole days from a to b. Uses UTC math on the parts, so a DST change never gives 23 or 25 hours. */
export function dayDiff(a: string, b: string): number {
  const [ay, am, ad] = a.split("-").map(Number);
  const [by, bm, bd] = b.split("-").map(Number);
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86_400_000);
}

/** The Monday on or before a day. */
export function mondayOf(day: string): string {
  const dow = localDate(day).getDay(); // 0 = Sunday
  return addDays(day, -((dow + 6) % 7));
}

/** "Nov 9" */
export function monthDay(day: string): string {
  const d = localDate(day);
  return `${d.toLocaleString("en-US", { month: "short" })} ${d.getDate()}`;
}

/** "Nov 9 – Nov 23" */
export function rangeLabel(r: Range): string {
  return `${monthDay(r.start)} – ${monthDay(r.end)}`;
}

export function overlaps(a: Range, b: Range): boolean {
  return a.start <= b.end && b.start <= a.end;
}

export function sameRange(a: Range, b: Range): boolean {
  return a.start === b.start && a.end === b.end;
}

/** A phase's plan as a Range, or null when the Plan line is unreadable. */
export function planOf(p: PhaseRecord): Range | null {
  return p.start && p.end ? { start: p.start, end: p.end } : null;
}

// ── which projects show ─────────────────────────────────────────────────────
export interface GanttProject {
  project: ProjectRecord;
  phases: PhaseRecord[]; // file order
  unphased: UnphasedMilestone[];
  doneCount: number;
}

export function phasesOf(projection: Projection): PhaseRecord[] {
  return projection.phases ?? [];
}

/** Projects with 1 or more phases, in the projection's order, optionally one domain. */
export function phasedProjects(
  projection: Projection,
  domain: Domain | "all" = "all"
): GanttProject[] {
  const byProject = new Map<string, PhaseRecord[]>();
  for (const p of phasesOf(projection)) {
    const list = byProject.get(p.project) ?? [];
    list.push(p);
    byProject.set(p.project, list);
  }
  const unphased = projection.unphasedMilestones ?? [];
  return projection.projects
    .filter((pr) => byProject.has(pr.slug) && (domain === "all" || pr.domain === domain))
    .map((pr) => {
      const phases = [...(byProject.get(pr.slug) ?? [])].sort((a, b) => a.order - b.order);
      return {
        project: pr,
        phases,
        unphased: unphased.filter((m) => m.project === pr.slug),
        doneCount: phases.filter((p) => p.state === "done").length,
      };
    });
}

/** The scope line. It always says that only phased projects show, and how many that is. */
export function scopeText(projection: Projection): string {
  const n = phasedProjects(projection).length;
  const m = projection.projects.length;
  return `Phased projects only: ${n} of ${m} active project${m === 1 ? "" : "s"}.`;
}

// ── chart range and geometry ────────────────────────────────────────────────
/**
 * From the Monday one week before the earliest open phase start (or today, if earlier)
 * to two weeks after the latest end. `extra` ranges (staged or pending) count as open.
 */
export function chartRange(
  phases: PhaseRecord[],
  today: string,
  extra: Range[] = []
): Range & { days: number } {
  const plans = phases.map(planOf);
  let anchor = today;
  phases.forEach((p, i) => {
    const r = plans[i];
    if (r && p.state !== "done" && r.start < anchor) anchor = r.start;
  });
  for (const r of extra) if (r.start < anchor) anchor = r.start;
  let last = addDays(today, 14);
  for (const r of [...plans, ...extra]) if (r && r.end > last) last = r.end;
  const start = addDays(mondayOf(anchor), -7);
  const end = addDays(last, 14);
  return { start, end, days: dayDiff(start, end) + 1 };
}

/** Mondays from the range start through its end. */
export function weeksIn(range: Range): string[] {
  const out: string[] = [];
  for (let d = mondayOf(range.start); d <= range.end; d = addDays(d, 7)) out.push(d);
  return out;
}

export function dayToX(day: string, range: Range, dayWidth: number): number {
  return dayDiff(range.start, day) * dayWidth;
}

/** Pixels → whole days, rounded to the nearest day (snap). */
export function pxToDays(px: number, dayWidth: number): number {
  return Math.round(px / dayWidth);
}

/** A bar's x and width. Both ends inclusive, so a one-day phase is one day wide. */
export function barBox(r: Range, range: Range, dayWidth: number): { x: number; w: number } {
  return { x: dayToX(r.start, range, dayWidth), w: (dayDiff(r.start, r.end) + 1) * dayWidth };
}

// ── drag math ───────────────────────────────────────────────────────────────
export type DragKind = "move" | "start" | "end";

/** Move or resize by whole days. The start never passes the end. */
export function dragRange(from: Range, kind: DragKind, deltaDays: number): Range {
  const d = Math.trunc(deltaDays) || 0;
  if (kind === "move") return { start: addDays(from.start, d), end: addDays(from.end, d) };
  if (kind === "start") {
    const s = addDays(from.start, d);
    return { start: s > from.end ? from.end : s, end: from.end };
  }
  const e = addDays(from.end, d);
  return { start: from.start, end: e < from.start ? from.start : e };
}

/** Keyboard: arrows 1 day, Shift 1 week, Alt/Option moves the end only. */
export function keyDelta(
  key: string,
  mods: { shift: boolean; alt: boolean }
): { kind: DragKind; days: number } | null {
  const sign = key === "ArrowRight" ? 1 : key === "ArrowLeft" ? -1 : 0;
  if (!sign) return null;
  return { kind: mods.alt ? "end" : "move", days: sign * (mods.shift ? 7 : 1) };
}

/** Why a phase cannot be dragged, or null when it can. */
export function dragBlock(p: PhaseRecord, pendingIds: Set<string>): string | null {
  if (p.problem) return `This phase has a problem (${p.problem}). Fix the note first.`;
  if (!planOf(p)) return "This phase has no readable plan.";
  if (p.state === "done") return "Done phases cannot move.";
  if (pendingIds.has(p.id)) return "A change to this phase is waiting to be applied.";
  return null;
}

// ── the four preview outputs ────────────────────────────────────────────────
/** 1. "Discover: Nov 9 – Nov 23 (was Nov 2 – Nov 16)." */
export function newRangeText(name: string, from: Range, to: Range): string {
  return `${name}: ${rangeLabel(to)} (was ${rangeLabel(from)}).`;
}

export interface OutsideMilestone {
  taskId: string;
  text: string;
  due: string;
  side: "before start" | "after end";
  already: boolean; // outside the old range too
}

function sideOf(due: string, r: Range): OutsideMilestone["side"] | null {
  if (due < r.start) return "before start";
  if (due > r.end) return "after end";
  return null;
}

/** 2. Open 📅 tasks in this phase that fall outside the new range. */
export function milestonesOutside(p: PhaseRecord, from: Range, to: Range): OutsideMilestone[] {
  const out: OutsideMilestone[] = [];
  for (const m of p.milestones) {
    if (!m.open) continue;
    const side = sideOf(m.due, to);
    if (!side) continue;
    out.push({
      taskId: m.taskId,
      text: m.text,
      due: m.due,
      side,
      already: sideOf(m.due, from) !== null,
    });
  }
  return out.sort((a, b) => a.due.localeCompare(b.due));
}

export interface OverlapPhase {
  id: string;
  name: string;
  range: Range;
  isNew: boolean; // did not overlap the old range
}

/** 3. Other phases in this project whose range overlaps the new range. */
export function overlapsIn(
  p: PhaseRecord,
  from: Range,
  to: Range,
  siblings: PhaseRecord[]
): OverlapPhase[] {
  const out: OverlapPhase[] = [];
  for (const s of siblings) {
    if (s.id === p.id || s.project !== p.project) continue;
    const r = planOf(s);
    if (!r || !overlaps(r, to)) continue;
    out.push({ id: s.id, name: s.name, range: r, isNew: !overlaps(r, from) });
  }
  return out;
}

export interface WeekCount {
  week: string; // the Monday
  count: number;
}

/**
 * Active phases per week across every phased project. Active = not done, and the range touches
 * that Monday–Sunday. `override` replaces one phase's range (the drag preview).
 */
export function weeklyCounts(
  phases: PhaseRecord[],
  weeks: string[],
  override?: { id: string; range: Range }
): WeekCount[] {
  return weeks.map((week) => {
    const w = { start: week, end: addDays(week, 6) };
    let count = 0;
    for (const p of phases) {
      if (p.state === "done") continue;
      const r = override && override.id === p.id ? override.range : planOf(p);
      if (r && overlaps(r, w)) count++;
    }
    return { week, count };
  });
}

export interface WeekChange extends WeekCount {
  before: number;
  changed: boolean;
}

/** 4. The weekly strip before → after, with the changed weeks marked. */
export function weeklyChange(
  phases: PhaseRecord[],
  weeks: string[],
  id: string,
  to: Range
): WeekChange[] {
  const before = weeklyCounts(phases, weeks);
  const after = weeklyCounts(phases, weeks, { id, range: to });
  return after.map((a, i) => ({
    ...a,
    before: before[i].count,
    changed: a.count !== before[i].count,
  }));
}

export interface Preview {
  title: string;
  outside: OutsideMilestone[];
  overlaps: OverlapPhase[];
  weeks: WeekChange[];
}

/** All four sections for one draft. Nothing else: no suggestion, dependency or capacity figure. */
export function buildPreview(
  p: PhaseRecord,
  from: Range,
  to: Range,
  all: PhaseRecord[],
  weeks: string[]
): Preview {
  return {
    title: newRangeText(p.name, from, to),
    outside: milestonesOutside(p, from, to),
    overlaps: overlapsIn(p, from, to, all),
    weeks: weeklyChange(all, weeks, p.id, to),
  };
}

// ── mismatch ────────────────────────────────────────────────────────────────
/** "M2 📅 Nov 16. Plan ends Nov 14." The flag is shown, never fixed. */
export function mismatchText(p: PhaseRecord): string | null {
  if (!p.mismatch) return null;
  const m = p.milestones.find((x) => x.taskId === p.mismatch?.taskId);
  const label = m ? shortMilestone(m.text) : "Milestone";
  return `${label} 📅 ${monthDay(p.mismatch.milestone)}. Plan ends ${monthDay(p.mismatch.planEnd)}.`;
}

/** "M2" from "M2 Discover base done", else the first 24 characters. */
export function shortMilestone(text: string): string {
  const m = text.match(/^(M\d+)\b/);
  if (m) return m[1];
  return text.length > 24 ? `${text.slice(0, 23)}…` : text;
}

// ── staging, undo, stale, batch ─────────────────────────────────────────────
export interface StagedPlan {
  phaseId: string;
  project: string;
  name: string;
  path: string;
  heading: string;
  raw: string;
  hash: string;
  from: Range;
  to: Range;
  stagedAt: string;
}

export interface PlanUndo {
  id: string;
  previous: StagedPlan | undefined;
  label: string;
  at: number;
}

/**
 * Stage a drop. A drop back on the plan removes the staged change. Returns the new items and
 * the entry that undoes exactly this step, or null when nothing changed.
 */
export function stagePlan(
  items: Record<string, StagedPlan>,
  p: PhaseRecord,
  to: Range,
  now = new Date()
): { items: Record<string, StagedPlan>; undo: PlanUndo } | null {
  const from = planOf(p);
  if (!from) return null;
  const prev = items[p.id];
  const next = { ...items };
  if (sameRange(from, to)) {
    if (!prev) return null;
    delete next[p.id];
  } else {
    if (prev && sameRange(prev.to, to)) return null;
    next[p.id] = {
      phaseId: p.id,
      project: p.project,
      name: p.name,
      path: p.path,
      heading: p.heading,
      raw: p.planRaw,
      hash: p.hash,
      from,
      to,
      stagedAt: now.toISOString(),
    };
  }
  const label = sameRange(from, to) ? `${p.name} back to plan` : `${p.name} → ${rangeLabel(to)}`;
  return { items: next, undo: { id: p.id, previous: prev, label, at: now.getTime() } };
}

export function undoPlan(
  items: Record<string, StagedPlan>,
  undo: PlanUndo
): Record<string, StagedPlan> {
  const next = { ...items };
  if (undo.previous) next[undo.id] = undo.previous;
  else delete next[undo.id];
  return next;
}

export function undoPlanLeft(undo: PlanUndo | null, now = Date.now()): number {
  if (!undo) return 0;
  return Math.max(0, UNDO_MS - (now - undo.at));
}

/** A staged change is stale when the phase is gone or its heading + Plan line changed in the vault. */
export function isStalePlan(s: StagedPlan, phases: PhaseRecord[]): boolean {
  const p = phases.find((x) => x.id === s.phaseId);
  return !p || p.hash !== s.hash;
}

export function toPlanDecision(s: StagedPlan): PlanDecisionInput {
  return {
    taskId: s.phaseId,
    path: s.path,
    heading: s.heading,
    raw: s.raw,
    hash: s.hash,
    action: "replan",
    from: { start: s.from.start, end: s.from.end },
    to: { start: s.to.start, end: s.to.end },
    stagedAt: s.stagedAt,
  };
}

/** One batch with every staged change that is not stale. Uses the P12 batch format (schema 1). */
export function buildPlanBatch(
  staged: StagedPlan[],
  projection: Projection,
  clientBatchId: string
): TriageBatchInput {
  const phases = phasesOf(projection);
  return {
    schema: 1,
    clientBatchId,
    seen: { projectionAt: projection.generatedAt },
    decisions: staged.filter((s) => !isStalePlan(s, phases)).map(toPlanDecision),
  };
}
